import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import config from "../config.js";
import { apiErrorMessage } from "../utils/apiError";
import { parseScanCode, scanLabel, targetForScan } from "../utils/scanCodes";

const API_BASE = config.API_BASE_URL;

/**
 * /s/:code — where a printed QR lands (2026-10-07). A phone camera opens the link; a
 * workstation scanner's link is routed here by the queue search boxes. Asks the API what
 * the code is, then replaces itself with the page for it (utils/scanCodes targetForScan),
 * so Back goes to wherever the person was before the scan, not to this page.
 */
export default function ScanResolvePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    const parsed = parseScanCode(code);
    if (!parsed) {
      setState({ phase: "done", message: `"${code}" isn't a JobQ code.`, links: [] });
      return undefined;
    }
    setState({ phase: "loading", parsed });
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/scan/resolve?code=${encodeURIComponent(parsed.code)}`);
        if (!res.ok) {
          // A 404 / 400 detail is already the sentence ("Task #12 is not in JobQ.").
          const msg = await apiErrorMessage(res, res.status >= 500 ? `Couldn't look up ${parsed.code}` : "");
          if (alive) setState({ phase: "done", message: msg, links: [], retry: res.status >= 500 });
          return;
        }
        const resolved = await res.json();
        if (!alive) return;
        const target = targetForScan(resolved);
        if (target.navigate) {
          navigate(target.navigate, { replace: true });
        } else {
          setState({ phase: "done", message: target.message, links: target.links || [] });
        }
      } catch (err) {
        if (alive) {
          setState({
            phase: "done",
            message: `Couldn't reach JobQ to look up ${parsed.code}. Check the connection and try again.`,
            links: [],
            retry: true,
          });
        }
      }
    })();
    return () => { alive = false; };
  }, [code, attempt, navigate]);

  if (state.phase === "loading") {
    return (
      <div className="text-center text-muted mt-5">
        <i className="fas fa-qrcode fa-2x d-block mb-3"></i>
        Opening {scanLabel(state.parsed)}…
      </div>
    );
  }

  return (
    <div className="mx-auto mt-4" style={{ maxWidth: 520 }}>
      <div className="card">
        <div className="card-body">
          <h5 className="card-title"><i className="fas fa-qrcode me-2"></i>Scanned code</h5>
          <p className="card-text">{state.message}</p>
          <div className="d-flex flex-wrap gap-2">
            {state.links.map((l) => (
              <Link key={l.to} to={l.to} replace className="btn btn-primary">{l.label}</Link>
            ))}
            {state.retry && (
              <button className="btn btn-outline-primary" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
            )}
            <Link to="/queue" className="btn btn-outline-secondary">Job Queue</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
